// Week-one report endpoints (ai-reporting /reporting/weekone/v1). Long steps (plan, revise, preview, write) return 202
// and run as a job on the report; the page polls getReport and shows job.step until job.state isn't "running".
//
// Mock mode (local only): REACT_APP_WEEK_ONE_MOCK=1, or open any week-one page with ?mock=1. It serves a synthetic
// member from mock/view.json and simulates the jobs, so the page works without the service or real member data.
import { WEEK_ONE_API_URL, IS_LOCAL } from "../../config/api.config";
import * as mock from "./mockApi";

const MOCK_KEY = "weekOneMock";

export function mockOn() {
  if (!IS_LOCAL) return false;
  if (process.env.REACT_APP_WEEK_ONE_MOCK === "1") return true;
  try {
    if (new URLSearchParams(window.location.search).get("mock") === "1") sessionStorage.setItem(MOCK_KEY, "1");
    return sessionStorage.getItem(MOCK_KEY) === "1";
  } catch {
    return false;
  }
}

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const TELL_US = "If it keeps happening, send this page's link to the AI team.";

// What the reviewer reads when a request fails: what happened and what to do, never a status code. The service's own
// 400/404/409 messages are written for the reviewer ("still needed: …"); anything else gets a plain sentence.
export function friendlyError(status, json = {}) {
  // status 0: no answer at all. Usually the service restarting (a deploy), so it doesn't blame the reviewer's connection.
  if (status === 0) return `Couldn't reach the report service. It may be restarting: wait a minute, then reload the page. ${TELL_US}`;
  if (status === 401) return `This page couldn't connect to the report service. Reload the page. ${TELL_US}`;
  if (status === 422) return `Your changes couldn't be saved. Reload the page and try again. ${TELL_US}`;
  if (status >= 500) return `Something went wrong on our side. Reload the page in a few minutes. ${TELL_US}`;
  return json.error || `Something went wrong. Reload the page and try again. ${TELL_US}`;
}

async function call(method, path, body, root = "/reports") {
  const headers = { "Content-Type": "application/json" };
  if (process.env.REACT_APP_WEEK_ONE_API_KEY) headers["X-Api-Key"] = process.env.REACT_APP_WEEK_ONE_API_KEY;
  let res;
  try {
    res = await fetch(`${WEEK_ONE_API_URL}${root}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  } catch {
    throw new ApiError(friendlyError(0), 0);
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(friendlyError(res.status, json), res.status);
  return json;
}

const real = {
  listReports: (memberId) => call("GET", `?memberId=${encodeURIComponent(memberId)}`).then((j) => j.reports),
  // The service picks the days from the member's timeline and dates the report today; coverage says what it will read.
  coverage: (memberId) => call("GET", `?memberId=${encodeURIComponent(memberId)}`, null, "/coverage"),
  startReport: (memberId) => call("POST", "", { memberId }).then((j) => j.reportId),
  // An error the page showed, sent to the service's logs (and alerts). Best effort: a failure here is ignored.
  logError: (details) => call("POST", "", details, "/client-log").catch(() => {}),
  getReport: (id) => call("GET", `/${id}`),
  savePlan: (id, plan) => call("PUT", `/${id}/plan`, { plan }).then((j) => j.plan),
  // before: what the change replaced, so undo goes back to before the change ({statuses: {name: old}} or {lever: old})
  revise: (id, { note = "", statuses = null, lever = null, before = null }) => call("POST", `/${id}/revise`, { note, statuses, lever, before }),
  undo: (id) => call("POST", `/${id}/undo`).then((j) => j.plan),
  preview: (id, note = "") => call("POST", `/${id}/preview`, note ? { note } : {}),
  approve: (id, { approverName, purpose, plan }) => call("POST", `/${id}/approve`, { approverName, purpose, plan }),
  retry: (id) => call("POST", `/${id}/retry`).then((j) => j.reportId),
  async pdfBlob(id, which) {
    const headers = {};
    if (process.env.REACT_APP_WEEK_ONE_API_KEY) headers["X-Api-Key"] = process.env.REACT_APP_WEEK_ONE_API_KEY;
    const res = await fetch(`${WEEK_ONE_API_URL}/reports/${id}/pdf?which=${which}`, { headers }).catch(() => {
      throw new ApiError(friendlyError(0), 0);
    });
    if (!res.ok) throw new ApiError(res.status === 404 ? `No ${which === "final" ? "final" : "preview"} PDF yet` : friendlyError(res.status), res.status);
    return res.blob();
  },
};

export function api() {
  return mockOn() ? mock : real;
}

// Opens a PDF in a new tab. The endpoint needs the API key header, so it's fetched here rather than linked.
export async function openPdf(id, which) {
  const tab = window.open("", "_blank");
  try {
    const blob = await api().pdfBlob(id, which);
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}
